import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { IDefinition } from "./AssistantDetailsContent";
import { useSpotUser } from "@/utils/spotUser";
import { useMemo } from "react";
import {
  KnowledgeBaseQaSettingsInput,
  WorkflowSettingsInput,
} from "@/generated/types.gen";

type ToolDetails = {
  workflowNames: Record<string, string>;
  knowledgeBaseNames: Record<string, string>;
  isLoading: boolean;
};

const useAssistantToolDetails = ({
  definition,
  isPublic,
}: {
  definition: Record<string, any> | undefined;
  isPublic: boolean | undefined;
}): ToolDetails => {
  const { workflow_ids, knowledge_base_ids } = useMemo(() => {
    if (isPublic) return { workflow_ids: [], knowledge_base_ids: [] };
    const workflow_ids = (definition as unknown as IDefinition)?.tools
      .filter((t) => t.type === "workflow" && t.enabled)
      .map((t) => (t as WorkflowSettingsInput).workflow_id);
    const knowledge_base_ids = (definition as unknown as IDefinition)?.tools
      .filter((t) => t.type === "kb_qa" && t.enabled)
      .map((t) => (t as KnowledgeBaseQaSettingsInput).kb_id);
    return { workflow_ids, knowledge_base_ids };
  }, [definition, isPublic]);

  const { spotGroup } = useSpotUser();

  const workflowIdsStr = useMemo(
    () => workflow_ids?.join(",") || "",
    [workflow_ids]
  );

  const kbIdsStr = useMemo(
    () => knowledge_base_ids?.join(",") || "",
    [knowledge_base_ids]
  );

  const { data: workflowsData, isLoading: isLoadingWorkflows } = useRemoteData(
    DATA_KEYS.WORKFLOWS,
    {
      path: { group_id: spotGroup?.id },
      query: { ids: workflowIdsStr },
    },
    {
      staleTime: Infinity,
      enabled: !!workflowIdsStr && !isPublic,
    }
  );

  const { data: kbData, isLoading: isLoadingKBs } = useRemoteData(
    DATA_KEYS.KNOWLEDGE_BASES_V2,
    {
      path: { group_id: spotGroup?.id },
      query: { ids: kbIdsStr },
    },
    {
      staleTime: Infinity,
      enabled: !!kbIdsStr && !isPublic,
    }
  );

  const workflowNames = useMemo(() => {
    const names: Record<string, string> = {};
    workflowsData?.items?.forEach((wf) => {
      names[wf.id] = wf.name;
    });
    return names;
  }, [workflowsData]);

  const knowledgeBaseNames = useMemo(() => {
    const names: Record<string, string> = {};
    kbData?.items?.forEach((kb) => {
      names[kb.id] = kb.name;
    });
    return names;
  }, [kbData]);

  return {
    workflowNames,
    knowledgeBaseNames,
    isLoading: isLoadingWorkflows || isLoadingKBs,
  };
};

export default useAssistantToolDetails;
